import { Private_Routes } from '@common/url';
import { signup } from '@common/user-analytics';
import { UserTypes } from '@common/UserTypes';
import { LogoDatabricksIcon, LogoDotsIcon, LogoProphecyIcon } from '@prophecy/icons';
import { EventName, EventType, metrics } from '@prophecy/metrics';
import { BaseColor, resolveCssVariable, theme } from '@prophecy/theme';
import { Button, Checkbox, Display, Input, Password, RadialIconContainer, Stack, Text } from '@prophecy/ui-v3';
import { StyledHelpText } from '@prophecy/ui-v3/Input/styled';
import { navigateAndRefresh } from '@prophecy/utils/history';
import { validateHTMLTags, validatePassword } from '@prophecy/utils/validation';
import { useMutation } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { useMixPanel } from '../../context/mixpanel/context';
import { setNewPasswordForDBUser } from '../../data/apis/api';
import { DatabrickUserPasswordPayload } from '../../data/apis/types';
import { errorHandler } from '../../data/util';
import { StyledForm, useSignupParamsFromURL } from './common';
import { PasswordError, StyledFormSection, StyledSuggestion } from './signup/Password';

const Form = styled(StyledForm)`
  font-family: ${theme.fontFamily.text};
  flex-direction: column;
  height: 100%;
  align-items: stretch;
  gap: ${theme.spaces.x48};
`;

const Heading = styled(Display)`
  margin-top: ${theme.spaces.x12};
`;

const StyledSigupPasswordSuggestion = styled(StyledSuggestion)`
  @media (max-width: ${resolveCssVariable(theme.breakpoints.s)}) {
    display: none;
  }
`;

const StretchedInput = styled(Input)`
  flex-grow: 1;
`;

const StyledDatabrickSignUp = styled(Stack)`
  flex-direction: row;
  @media (max-width: ${resolveCssVariable(theme.breakpoints.s)}) {
    flex-direction: column;
    padding: ${theme.spaces.x16};
  }
`;

export default function DatabrickSignUp() {
  const { track, identifyAndSetPeople } = useMixPanel();
  const {
    firstName,
    lastName,
    teamName,
    nameInputNeeded,
    email,
    token,
    teamUID,
    uid,
    company,
    teamNameUpdationAllowed,
    strTeamNameUpdationAllowed
  } = useSignupParamsFromURL();

  const { isLoading, mutateAsync } = useMutation<{ message?: string }, unknown, DatabrickUserPasswordPayload>(
    (body) => setNewPasswordForDBUser(body, { 'x-auth-token': token }),
    {
      onError: errorHandler
    }
  );
  useEffect(() => {
    if (email) {
      identifyAndSetPeople(email, {
        firstName,
        lastName,
        company,
        signup_date: new Date().toISOString(),
        source: UserTypes.DatabricksPartner,
        userId: uid
      });
      track(signup.databricks.pageLanding, { email });
      metrics.track({ eventName: EventName.ui.page.signup.databricksLandingPage, eventType: EventType.userAction });
    }
  }, [email, firstName, lastName, track, identifyAndSetPeople, uid, company]);

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName,
      lastName,
      password: '',
      company: '',
      confirmPassword: '',
      teamName: teamName,
      agree: true,
      sendpromotionalemail: true
    }
  });
  async function onSubmit({
    password,
    teamName,
    sendpromotionalemail,
    firstName,
    lastName,
    company
  }: {
    password: string;
    teamName: string;
    firstName: string;
    lastName?: string;
    company: string;
    sendpromotionalemail: boolean;
  }) {
    const signup_date = new Date().toISOString();
    identifyAndSetPeople(email, {
      firstName,
      lastName,
      company,
      source: UserTypes.DatabricksPartner,
      signup_date
    });
    track(signup.databricks.createPasswordSuccess, { firstName, lastName, company, signup_date });
    metrics.track({ eventName: EventName.ui.page.dbsignup.createPasswordSuccess, eventType: EventType.userAction });
    let data: DatabrickUserPasswordPayload = {
      identifier: email,
      password,
      firstName,
      company,
      lastName,
      sendpromotionalemail
    };
    if (teamNameUpdationAllowed) {
      data = { ...data, teamUID, teamName, token, email, teamNameUpdationAllowed: strTeamNameUpdationAllowed };
    }
    await mutateAsync(data);
    navigateAndRefresh(Private_Routes.Home.getUrl());
  }
  return (
    <StyledDatabrickSignUp height='100%' width='100%' gap={theme.spaces.x24}>
      <StyledFormSection grow='1' shrink='0'>
        <Stack width='490px'>
          <Form onSubmit={handleSubmit(onSubmit)} autoComplete='off'>
            <Stack alignY='center' gap={theme.spaces.x12}>
              <Stack direction='horizontal' gap={theme.spaces.x12} alignY='center'>
                <RadialIconContainer size={theme.spaces.x64} tone={BaseColor.primary}>
                  <LogoProphecyIcon />
                </RadialIconContainer>
                <LogoDotsIcon />
                <RadialIconContainer size={theme.spaces.x64} tone={BaseColor.primary}>
                  <LogoDatabricksIcon />
                </RadialIconContainer>
              </Stack>
              <Heading level='sm' weight={theme.fontWeight.semiBold}>
                Connect Prophecy to Databricks
              </Heading>
              <Text level='md'>The connection details have been generated for {email}.</Text>
            </Stack>
            <Stack gap={theme.spaces.x32}>
              <Stack gap={theme.spaces.x20}>
                <Controller
                  rules={{
                    required: 'Company Name is required'
                  }}
                  render={({ field }) => (
                    <Input
                      label='Company'
                      required
                      {...field}
                      variant={errors.company ? 'error' : 'primary'}
                      helpText={errors.company?.message}
                      data-test-id='databricks-signup-company-input'
                    />
                  )}
                  control={control}
                  name='company'
                />
                {(nameInputNeeded || !firstName) && (
                  <Stack direction='horizontal' gap={theme.spaces.x8}>
                    <Controller
                      render={({ field }) => (
                        <StretchedInput
                          type='text'
                          label='First Name'
                          required
                          placeholder='Enter your first name'
                          {...field}
                          variant={errors.firstName ? 'error' : 'primary'}
                          helpText={errors.firstName?.message}
                          data-test-id='databricks-signup-first-name-input'
                        />
                      )}
                      control={control}
                      name='firstName'
                      rules={{
                        validate: validateHTMLTags,
                        required: 'First Name is required'
                      }}
                    />
                    <Controller
                      render={({ field }) => (
                        <StretchedInput
                          type='text'
                          label='Last Name'
                          placeholder='Enter your name'
                          {...field}
                          data-test-id='databricks-signup-last-name-input'
                        />
                      )}
                      rules={{
                        validate: validateHTMLTags
                      }}
                      control={control}
                      name='lastName'
                    />
                  </Stack>
                )}
                <Controller
                  render={({ field }) => (
                    <Password
                      placeholder='Create password'
                      iconPlacement='right'
                      {...field}
                      variant={errors.password ? 'error' : 'primary'}
                      data-test-id='databricks-signup-password-input'
                    />
                  )}
                  control={control}
                  name='password'
                  rules={{
                    required: 'Password is required',
                    validate: (value) => {
                      const result = validatePassword(value);
                      return Object.values(result).some((v) => v === false) ? false : true;
                    }
                  }}
                />
                <Controller
                  render={({ field }) => (
                    <Password
                      placeholder='Confirm password'
                      iconPlacement='right'
                      {...field}
                      variant={errors.confirmPassword ? 'error' : 'primary'}
                      helpText={errors.confirmPassword?.message}
                      data-test-id='databricks-signup-confirm-password-input'
                    />
                  )}
                  control={control}
                  name='confirmPassword'
                  rules={{
                    validate: (value) => (value === getValues('password') ? true : 'Passwords do not match')
                  }}
                />
              </Stack>
              {teamNameUpdationAllowed ? (
                <Controller
                  render={({ field }) => (
                    <Input
                      label='Team name'
                      placeholder='Team Name'
                      {...field}
                      variant={errors.teamName ? 'error' : 'primary'}
                      helpText={errors.teamName?.message}
                      data-test-id='databricks-signup-team-name-input'
                    />
                  )}
                  control={control}
                  name='teamName'
                  rules={{
                    required: 'Team is required'
                  }}
                />
              ) : null}
              <Button type='submit' size='xl' loading={isLoading} data-test-id='databricks-signup-submit-button'>
                Connect
              </Button>
              <Controller
                render={({ field: { value, ...field } }) => (
                  <>
                    <Stack direction='horizontal' gap={theme.spaces.x6}>
                      <Checkbox checked={value} {...field}>
                        <Text level='sm' weight={theme.fontWeight.medium} tone={theme.colors.gray700}>
                          I agree to the{' '}
                          <a
                            href='https://legal.prophecy.io/terms'
                            target='_blank'
                            rel='noreferrer'
                            data-test-id='prophecy-sign-up-terms-of-service-link'>
                            Terms of Service
                          </a>{' '}
                          and{' '}
                          <a
                            href='https://legal.prophecy.io/privacy'
                            target='_blank'
                            rel='noreferrer'
                            data-test-id='prophecy-sign-up-privacy-policy-link'>
                            Privacy Policy
                          </a>
                          .
                        </Text>
                      </Checkbox>
                    </Stack>

                    {errors.agree?.message && (
                      <StyledHelpText
                        variant={errors.agree ? 'error' : 'primary'}
                        direction='horizontal'
                        align='space-between'>
                        {errors.agree?.message}
                      </StyledHelpText>
                    )}
                  </>
                )}
                control={control}
                name='agree'
                rules={{
                  required: 'Please agree to the terms and conditions'
                }}
              />
              <Controller
                render={({ field: { value, ...field } }) => (
                  <>
                    <Stack direction='horizontal' gap={theme.spaces.x6}>
                      <Checkbox checked={value} {...field}>
                        <Text level='sm' weight={theme.fontWeight.medium} tone={theme.colors.gray700}>
                          Keep me updated with the latest Prophecy announcements
                        </Text>
                      </Checkbox>
                    </Stack>
                  </>
                )}
                control={control}
                name='sendpromotionalemail'
              />
            </Stack>
          </Form>
        </Stack>
      </StyledFormSection>
      <StyledSigupPasswordSuggestion grow='0' shrink='0'>
        <PasswordError password={watch('password')} />
      </StyledSigupPasswordSuggestion>
    </StyledDatabrickSignUp>
  );
}
